import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Box,
} from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import { styled } from '@mui/material/styles';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ClassRoundedIcon from '@mui/icons-material/ClassRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/AuthContext';
import { useInstitution } from '../../components/InstitutionContext';
import { userHasInstitutionRole } from '../../utils/permissions';
import getUserDisplayName from '../../utils/getUserDisplayName';

const StyledListHeader = styled(ListSubheader)({
  backgroundImage: 'var(--Paper-overlay)',
});

// Top section of the menu
const primaryItems = [
  { text: 'Home', icon: <HomeRoundedIcon />, path: '/' },
  { text: 'Courses', icon: <ClassRoundedIcon color="info" />, path: '/courses' },
  { text: 'Community', icon: <ForumRoundedIcon color="success" />, path: '/community' },
  { text: 'Tutoring', icon: <SchoolRoundedIcon color="secondary" />, path: '/tutors' },
];

// Items displayed at the bottom of the menu, above the user info
const secondaryItems = [
  { text: 'Settings', icon: <SettingsRoundedIcon />, path: '/settings' },
  { text: 'About', icon: <InfoRoundedIcon />, path: '/about' },
  { text: 'Feedback', icon: <HelpRoundedIcon />, path: '/feedback' },
];

const adminItems = (user, institution) => {
  const items = [];
  const isCoursesAdmin = userHasInstitutionRole(
    user,
    'CoursesAdminTeam',
    institution
  );
  const isCommunityAdmin = userHasInstitutionRole(
    user,
    'CommunityAdminTeam',
    institution
  );

  if (isCoursesAdmin) {
    items.push({
      text: 'Course Admin',
      icon: <AdminPanelSettingsIcon color="info" />,
      path: '/courses/admin-dashboard',
    });
  }

  if (isCommunityAdmin) {
    items.push({
      text: 'Community Admin',
      icon: <AdminPanelSettingsIcon color="success" />,
      path: '/community/admin-dashboard',
    });
  }

  return items;
};


export default function MenuContent() {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const location = useLocation(); // Get the current path

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Special case: when on the course admin dashboard don't highlight
    // the general "Courses" menu item.
    if (
      path === '/courses' &&
      location.pathname.startsWith('/courses/admin-dashboard')
    ) {
      return false;
    }
    if (
      path === '/courses' &&
      location.pathname.startsWith('/courses/my-submissions')
    ) {
      return false;
    }
    if (
      path === '/community' &&
      location.pathname.startsWith('/community/admin-dashboard')
    ) {
      return false;
    }
    if (
      path === '/community' &&
      location.pathname.startsWith('/community/user')
    ) {
      return false;
    }
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + '/')
    );
  };

  const admin = adminItems(user, selectedInstitution);

  return (
    <Stack sx={{ flexGrow: 1, p: 1, gap: 1 }}>
      {/* Main List */}
      <List dense>
        {primaryItems.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={NavLink}
                to={item.path}
                selected={isActive(item.path)} // Highlight based on active path
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
            
          </React.Fragment>
        ))}
      </List>

      {user && (
        <>
          <List dense>
            <StyledListHeader>MY PAGES</StyledListHeader>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={NavLink}
                to="/courses/my-submissions"
                selected={isActive('/courses/my-submissions')}
              >
                <ListItemIcon>
                  <ClassRoundedIcon color="info" />
                </ListItemIcon>
                <ListItemText primary="Course Submissions" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={NavLink}
                to={`/community/user/${encodeURIComponent(
                  getUserDisplayName(user),
                )}`}
                selected={isActive(
                  `/community/user/${encodeURIComponent(
                    getUserDisplayName(user),
                  )}`,
                )}
              >
                <ListItemIcon>
                  <ForumRoundedIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Community Posts" />
              </ListItemButton>
            </ListItem>
            {user?.tutor_profile && (
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  component={NavLink}
                  to={`/tutors/${user.tutor_profile.id}`}
                  selected={isActive(`/tutors/${user.tutor_profile.id}`)}
                >
                  <ListItemIcon>
                    <SchoolRoundedIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText primary="Tutor Profile" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </>
      )}

      {admin.length > 0 && (
        <>
          <List dense>
            <StyledListHeader>ADMIN CONTROL</StyledListHeader>
            {admin.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  component={NavLink}
                  to={item.path}
                  selected={isActive(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Push secondary items to the bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Secondary List */}
      <List dense>
        {/* <StyledListHeader>More</StyledListHeader> */}
        {secondaryItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component={NavLink}
              to={item.path}
              selected={isActive(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

    </Stack>
  );
}
